import {
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import classNames from "classnames";
import lodash from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  Mesh,
  type ModelMesh,
  type ModelNodeMesh,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./EntityConfig.module.css";
import { JointBindings } from "./JointBindings";
import { ModelImportControl } from "./ModelImportControl";

interface Props {
  entity: Entity;
  panelId: string;
  /**
   * Whether a model may be *authored* here — `release-3d-assets`, read once by
   * `ThreeDPanelSettings` and passed down.
   *
   * A prop rather than a `useCheckFeatureFlag` call because the flag hook
   * reaches for domain services, and this form is deliberately a plain set of
   * controls any tree can render — the same reason `ModelImportControl` was
   * split out of it.
   *
   * Only authoring is gated. A model an entity already has keeps rendering, so
   * a user without the flag still sees a colleague's robot.
   */
  modelMeshEnabled: boolean;
}

/**
 * What the type control offers, which is the mesh types plus the absence of one.
 *
 * `"none"` is not a mesh type in the schema — a meshless entity simply has no
 * `mesh` component. It needs a name here so an entity that draws nothing has
 * something to display, and so removing geometry is expressible.
 */
type MeshKind = Mesh["type"] | "none";

/**
 * What to call an imported model on screen.
 *
 * The file the mesh references is not necessarily the file the user chose: for
 * an ingested robot it is a glTF the platform generated, living at a derived
 * path under `.VISUALIZATION_ASSETS/` that the user has never seen and cannot
 * act on. The source they picked is recorded next to it precisely so it can be
 * named here instead — showing the artifact's path, even as a caption or a
 * tooltip, would be showing an implementation detail.
 *
 * Only a whole-model mesh can carry a source: a `model_node` names a node
 * *within* a file that some model already brought in, and falls back to that
 * file's own path.
 *
 * A `model` without one falls back too. Every model added today has a source —
 * the picker offers only ingested robot descriptions — so a sourceless one is a
 * workspace saved earlier: before `source` existed, or while a plain
 * `.gltf`/`.glb` could still be picked. Both keep working, and the fallback is
 * what shows them a name rather than a bare file id.
 */
function modelDisplayName(mesh: ModelMesh | ModelNodeMesh): string {
  const source = mesh.type === "model" ? mesh.source : undefined;
  return source?.relativePath ?? mesh.file.relativePath ?? mesh.file.fileId;
}

export function MeshConfig({ entity, panelId, modelMeshEnabled }: Props) {
  const dispatch = useVizDispatch();
  // Mounted only while a model is being chosen, so the domain services and org
  // context that importing needs are not a dependency of the shape controls.
  const [choosingModel, setChoosingModel] = React.useState(false);

  // Mesh settings
  const committedType: MeshKind = entity.mesh?.type ?? "none";
  const [meshType, setMeshType] = React.useState<MeshKind>(committedType);

  // Follows the entity when its mesh is replaced from outside this form — which
  // an import does, writing a `model` over whatever the container had. Without
  // this the control keeps showing the old type, and the effect below promptly
  // re-derives it and undoes the import.
  const lastCommittedRef = React.useRef(committedType);
  React.useEffect(
    function onMeshReplacedElsewhere() {
      if (lastCommittedRef.current !== committedType) {
        lastCommittedRef.current = committedType;
        setMeshType(committedType);
      }
    },
    [committedType],
  );
  const [radius, setRadius] = React.useState(
    `${entity.mesh?.type === "sphere" ? entity.mesh.radius : 0.25}`,
  );
  const [radiusError, setRadiusError] = React.useState("");
  const [size, setSize] = React.useState(
    `${entity.mesh?.type === "box" ? entity.mesh.size : 0.5}`,
  );
  const [sizeError, setSizeError] = React.useState("");
  const [color, setColor] = React.useState(
    // `model_node` geometry keeps the materials its glTF defined, so it has no
    // colour of its own for this control to seed from.
    (entity.mesh?.type === "sphere" || entity.mesh?.type === "box"
      ? entity.mesh.color
      : undefined) || "#4b77b4",
  );

  React.useEffect(
    function onMeshFieldsChanged() {
      let updatedMesh: Mesh;
      if (meshType === "model") {
        // A model mesh names a file and carries the joints read out of it, all
        // of which the import writes. Deriving one from the shape controls here
        // would claim a model had been imported when no file has been chosen.
        return;
      }
      if (meshType === "model_node") {
        // Geometry imported from a glTF is defined by its `file`/`nodeName`
        // reference, which this form has no controls for and must not invent.
        // Deriving a mesh from the shape controls here — as every other branch
        // does — would overwrite the reference with a default box the moment the
        // row was expanded.
        return;
      }
      if (meshType === "none") {
        if (entity.mesh !== undefined) {
          dispatch(updateThreeDEntity(panelId, entity.id, { mesh: undefined }));
        }
        return;
      }
      if (meshType === "sphere") {
        setRadiusError("");
        const parsedRadius = Number.parseFloat(radius);
        if (!Number.isFinite(parsedRadius)) {
          setRadiusError("Radius must be a number");
          return;
        } else if (parsedRadius <= 0) {
          setRadiusError("Radius must be positive");
          return;
        }
        updatedMesh = {
          type: "sphere",
          color,
          radius: parsedRadius,
        };
      } /* if (meshType === "box") */ else {
        setSizeError("");
        const parsedSize = Number.parseFloat(size);
        if (!Number.isFinite(parsedSize)) {
          setSizeError("Size must be a number");
          return;
        } else if (parsedSize <= 0) {
          setSizeError("Size must be positive");
          return;
        }
        updatedMesh = {
          type: "box",
          color,
          size: parsedSize,
        };
      }
      if (!lodash.isEqual(updatedMesh, entity.mesh)) {
        dispatch(updateThreeDEntity(panelId, entity.id, { mesh: updatedMesh }));
      }
    },
    [
      meshType,
      color,
      radius,
      size,
      entity,
      dispatch,
      panelId,
      setRadiusError,
      setSizeError,
    ],
  );

  const onTypeChanged = React.useCallback(
    (next: MeshKind) => {
      setMeshType(next);
      // Choosing a model is choosing a file, so the pick is the rest of the
      // gesture. Not when the entity already draws one: there the type has not
      // changed, and choosing a *different* model is the Model File field below.
      if (next === "model" && entity.mesh?.type !== "model") {
        setChoosingModel(true);
      }
    },
    [entity.mesh?.type],
  );

  /** Re-picking: the Model File field is the control that opens the picker. */
  const onModelFileActivated = React.useCallback(() => {
    setChoosingModel(true);
  }, []);

  // A read-only input still takes focus, so the field is reachable without a
  // mouse. Enter and Space are what activate a control there.
  const onModelFileKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setChoosingModel(true);
    }
  }, []);

  const onImportCancelled = React.useCallback(() => {
    setChoosingModel(false);
    // Nothing was imported, so leaving the control reading "Model" would claim
    // otherwise. Snap it back to what the entity actually has.
    setMeshType(entity.mesh?.type ?? "none");
  }, [entity.mesh?.type]);

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Mesh
      </RobotoTypography>
      <div className={styles.container}>
        {/*<ConfigItem label="Mesh Type" />*/}
        <FormControl size="small" fullWidth className={styles.field}>
          <InputLabel id="mesh-type-label" className={styles.input}>
            Mesh Type
          </InputLabel>
          <Select
            label={"Mesh Type"}
            labelId={"mesh-type-label"}
            value={meshType}
            onChange={(e) => onTypeChanged(e.target.value as MeshKind)}
            className={styles.input}
            inputProps={{ className: styles.input }}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="sphere">Sphere</MenuItem>
            <MenuItem value="box">Box</MenuItem>
            {/* Picking this opens the file browser: a model *is* its file, and
                this one entity then draws the file's whole node hierarchy.

                Withdrawn by the release flag — but never from an entity that
                already has a model, the same way `model_node` below is offered
                when it is what the entity has. Dropping it unconditionally
                would leave the Select's value out of range, so MUI would warn
                and render an empty control for precisely the users with the
                most to lose. */}
            {(modelMeshEnabled || entity.mesh?.type === "model") && (
              <MenuItem value="model">Model</MenuItem>
            )}
            {/* Only offered while it is what the entity already has: a model
                node cannot be authored here, since choosing one means choosing a
                file and a node within it. Switching away is therefore one-way —
                re-importing the model is how to get it back. */}
            {entity.mesh?.type === "model_node" && (
              <MenuItem value="model_node">Model node</MenuItem>
            )}
          </Select>
        </FormControl>

        {choosingModel && (
          <ModelImportControl
            entityId={entity.id}
            panelId={panelId}
            onImported={() => setChoosingModel(false)}
            onCancelled={onImportCancelled}
          />
        )}

        {meshType === "model" && entity.mesh?.type === "model" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Model File"
              size="small"
              margin="dense"
              fullWidth
              value={modelDisplayName(entity.mesh)}
              // Not typed into — a model is a file, so the field opens the same
              // picker the first import used and shows what was chosen. Re-picking
              // is safe because a model is one component: the import replaces this
              // entity's mesh and adds no entities, so there is nothing to collide
              // with. What it means for the joint bindings is `planModelRepick`.
              InputProps={{
                readOnly: true,
                onClick: onModelFileActivated,
                className: classNames(styles.input, styles.pickerField),
              }}
              InputLabelProps={{ className: styles.input }}
              inputProps={{
                className: styles.input,
                onKeyDown: onModelFileKeyDown,
              }}
              className={styles.input}
              helperText="Click to choose a different model"
            />
          </div>
        )}

        {entity.mesh?.type === "model" && entity.mesh.joints !== undefined && (
          <JointBindings
            joints={entity.mesh.joints}
            entityId={entity.id}
            mesh={entity.mesh}
            panelId={panelId}
          />
        )}

        {meshType === "model_node" && entity.mesh?.type === "model_node" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Model File"
              size="small"
              margin="dense"
              fullWidth
              value={modelDisplayName(entity.mesh)}
              InputProps={{ readOnly: true, className: styles.input }}
              InputLabelProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
              className={styles.input}
            />
            <TextField
              label="Model Node"
              size="small"
              margin="dense"
              fullWidth
              value={entity.mesh.nodeName}
              InputProps={{ readOnly: true, className: styles.input }}
              InputLabelProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
              className={styles.input}
            />
          </div>
        )}
        {meshType === "sphere" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Radius"
              autoComplete="off"
              type="number"
              size="small"
              margin="dense"
              fullWidth
              value={radius}
              error={!!radiusError}
              helperText={radiusError}
              onChange={(e) => setRadius(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0.1, step: 0.1, className: styles.input }}
              className={styles.input}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              fullWidth
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className={styles.input}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
            />
          </div>
        )}

        {meshType === "box" && (
          <div className={styles.fieldRow}>
            <TextField
              label="Length"
              autoComplete="off"
              type="number"
              size="small"
              margin="dense"
              fullWidth
              value={size}
              error={!!sizeError}
              helperText={sizeError}
              onChange={(e) => setSize(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0.1, step: 0.1, className: styles.input }}
              className={styles.input}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              fullWidth
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className={styles.input}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
            />
          </div>
        )}
      </div>
    </>
  );
}
